import React, { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { X, Car, CarFront, Clock, Check, AlertTriangle, Smartphone, Banknote, Users, ArrowLeft } from 'lucide-react';
import { RIDE_TYPES, formatCurrency } from '../../mock/mockData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fare estimates use vehicle_type 'bike' while RIDE_TYPES uses id 'motorcycle'.
const VEHICLE_ALIAS = { bike: 'motorcycle', moto: 'motorcycle', motorbike: 'motorcycle' };
const findRideInfo = (vehicleType) =>
  RIDE_TYPES.find((r) => r.id === vehicleType || r.id === VEHICLE_ALIAS[vehicleType]);

/**
 * Ride Selection Modal - Shows after dropoff confirmation
 * Displays all ride types with ETA and fare
 */
const RideSelectionModal = ({ 
  isOpen, 
  onClose, 
  estimates = [],
  pickupLocation = null,
  onConfirmRide,
  isLoading = false,
  compact = false,
  paymentMethod = 'mobile_money',
  onPaymentMethodChange = () => {},
  hasActiveDiscount = false,
  activeDiscountInfo = null,
  familyCreditBalance = 0,
  applyFamilyCredit = false,
  onApplyFamilyCreditChange = () => {},
  personalCreditBalance = 0,
  applyPersonalCredit = false,
  onApplyPersonalCreditChange = () => {}
}) => {
  const [selectedRideType, setSelectedRideType] = useState('economy');
  const [step, setStep] = useState('ride'); // 'ride' -> 'payment'
  const [nearby, setNearby] = useState(null); // {count, closest_km, eta_min}
  const isCash = paymentMethod === 'cash';
  const showCashDiscountWarning = isCash && hasActiveDiscount;

  // Always start on the ride-selection step whenever the sheet is opened.
  useEffect(() => {
    if (isOpen) setStep('ride');
  }, [isOpen]);

  // Heads-up: fetch how many drivers are online near the pickup for the
  // currently-selected vehicle type. Refreshes on selection change.
  useEffect(() => {
    if (!isOpen || !pickupLocation?.lat || !pickupLocation?.lng) return undefined;
    const ctrl = new AbortController();
    const url = `${API_URL}/api/rides/nearby-drivers?latitude=${pickupLocation.lat}&longitude=${pickupLocation.lng}&vehicle_type=${selectedRideType}`;
    fetch(url, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((d) => { if (d?.success) setNearby(d); })
      .catch(() => { /* ignore aborts */ });
    return () => ctrl.abort();
  }, [isOpen, pickupLocation?.lat, pickupLocation?.lng, selectedRideType]);

  if (!isOpen) return null;

  const getRideIcon = (type) => {
    switch (type) {
      case 'economy':
        return <Car className="w-6 h-6 text-blue-600" />;
      case 'premium':
        return <CarFront className="w-6 h-6 text-purple-600" />;
      case 'bike':
        return (
          <svg className="w-6 h-6 text-orange-600" viewBox="0 0 24 24" fill="currentColor">
            <path d="M5 20.5c-2.483 0-4.5-2.015-4.5-4.5s2.017-4.5 4.5-4.5c1.438 0 2.712.675 3.538 1.724L9 13.5l.948-2H8.5V9h3l1.406 3.698c.668-1.02 1.822-1.698 3.094-1.698 2.483 0 4.5 2.015 4.5 4.5s-2.017 4.5-4.5 4.5-4.5-2.015-4.5-4.5c0-.524.091-1.028.255-1.494L10.5 12.5l-1.812 4.43C7.758 18.355 6.464 20.5 5 20.5z M5 14c-1.379 0-2.5 1.122-2.5 2.5s1.121 2.5 2.5 2.5 2.5-1.122 2.5-2.5S6.379 14 5 14zm11 0c-1.379 0-2.5 1.122-2.5 2.5s1.121 2.5 2.5 2.5 2.5-1.122 2.5-2.5-1.121-2.5-2.5-2.5zM7.5 4.5H11L9 8H7.5V4.5z" />
          </svg>
        );
      default:
        return <Car className="w-6 h-6 text-gray-600" />;
    }
  };

  const getRideColor = (type) => {
    switch (type) {
      case 'economy':
        return 'bg-green-50 border-green-500';
      case 'premium':
        return 'bg-blue-50 border-blue-500';
      case 'bike':
        return 'bg-orange-50 border-orange-500';
      default:
        return 'bg-gray-50 border-gray-500';
    }
  };

  const getSelectedEstimate = () => {
    return estimates.find(e => e.vehicle_type === selectedRideType) || estimates[0];
  };

  // Heads-up banner shown in both compact and full modal.
  const nearbyBanner = (() => {
    if (!nearby) return null;
    if (nearby.count === 0) {
      return (
        <div className="mx-1 mb-2 rounded-2xl bg-amber-50 border border-amber-100 px-3 py-2 flex items-center gap-2" data-testid="nearby-drivers-banner-empty">
          <Users className="w-4 h-4 text-amber-700 shrink-0" />
          <p className="text-[12px] text-amber-800">
            <span className="font-semibold">No drivers online</span> within {nearby.radius_km} km — you may wait longer than usual.
          </p>
        </div>
      );
    }
    return (
      <div className="mx-1 mb-2 rounded-2xl bg-emerald-50 border border-emerald-100 px-3 py-2 flex items-center gap-2" data-testid="nearby-drivers-banner">
        <Users className="w-4 h-4 text-emerald-700 shrink-0" />
        <p className="text-[12px] text-emerald-800">
          <span className="font-semibold tabular-nums">{nearby.count}</span> driver{nearby.count === 1 ? '' : 's'} online within {nearby.radius_km} km
          {nearby.closest_km != null && (
            <> · closest <span className="font-semibold tabular-nums">{nearby.closest_km} km</span>{nearby.eta_min != null && <> · ~{nearby.eta_min} min away</>}</>
          )}
        </p>
      </div>
    );
  })();

  // Compact mode: bottom sheet style
  if (compact) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white rounded-t-3xl shadow-2xl max-h-[40vh] overflow-hidden">
        {/* Handle bar */}
        <div className="flex justify-center pt-2 pb-3">
          <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
        </div>

        {/* Compact Ride Options */}
        <div className="px-4 pb-4 overflow-y-auto max-h-[35vh]">
          {nearbyBanner}
          {isLoading ? (
            <div className="py-4 text-center">
              <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-xs text-gray-600">Calculating fares...</p>
            </div>
          ) : (
            <div className="space-y-2">
              {estimates.map((estimate) => {
                const isSelected = selectedRideType === estimate.vehicle_type;
                const rideInfo = findRideInfo(estimate.vehicle_type);
                
                return (
                  <button
                    key={estimate.vehicle_type}
                    onClick={() => {
                      setSelectedRideType(estimate.vehicle_type);
                      onConfirmRide(estimate);
                    }}
                    className={`w-full p-3 rounded-xl transition-all flex items-center justify-between ${
                      isSelected 
                        ? 'bg-green-50 border-2 border-green-500' 
                        : 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:border-green-300 dark:hover:border-green-400'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${isSelected ? 'bg-white' : 'bg-white'}`}>
                        {getRideIcon(estimate.vehicle_type)}
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-sm text-gray-800 dark:text-white">
                          {rideInfo?.name || estimate.vehicle_type}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {Math.round(estimate.eta)} min · {estimate.distance.toFixed(1)} km
                        </p>
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900 dark:text-white">
                        K{Math.round(estimate.price).toLocaleString()}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Regular mode: full modal — two steps: (1) choose ride, (2) payment.
  const selected = getSelectedEstimate();
  const selectedInfo = selected ? findRideInfo(selected.vehicle_type) : null;
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center pb-20">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      {/* Modal - floats higher above navigation */}
      <Card className="relative w-full max-w-md mx-4 max-h-[75vh] overflow-hidden flex flex-col shadow-xl bg-white dark:bg-gray-800">
        {/* Header */}
        <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {step === 'payment' && (
              <button
                data-testid="payment-back-btn"
                onClick={() => setStep('ride')}
                className="p-1.5 -ml-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-800 dark:text-white" />
              </button>
            )}
            <h2 className="text-lg font-bold text-gray-800 dark:text-white">
              {step === 'payment' ? 'Payment' : 'Choose Your Ride'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <X className="w-4 h-4 text-gray-800 dark:text-white" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {step === 'ride' ? (
            <>
              {nearbyBanner}
              {isLoading ? (
                <div className="py-6 text-center">
                  <div className="w-10 h-10 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-sm text-gray-600">Calculating fares...</p>
                </div>
              ) : (
                estimates.map((estimate) => {
                  const isSelected = selectedRideType === estimate.vehicle_type;
                  const rideInfo = findRideInfo(estimate.vehicle_type);

                  return (
                    <Card
                      key={estimate.vehicle_type}
                      onClick={() => setSelectedRideType(estimate.vehicle_type)}
                      className={`p-3 cursor-pointer transition-all relative bg-white dark:bg-gray-700 ${
                        isSelected 
                          ? `${getRideColor(estimate.vehicle_type)} border-2` 
                          : 'border dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        {/* Left: Icon & Name */}
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg ${isSelected ? 'bg-white' : 'bg-gray-50'}`}>
                            {getRideIcon(estimate.vehicle_type)}
                          </div>
                          <div>
                            <h3 className="font-semibold text-sm text-gray-800 dark:text-white">
                              {rideInfo?.name || estimate.vehicle_type}
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{rideInfo?.capacity || ''}</p>
                          </div>
                        </div>

                        {/* Right: ETA & Price */}
                        <div className="text-right">
                          <div className="flex items-center justify-end space-x-1 mb-0.5">
                            <Clock className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                              {Math.round(estimate.eta)} min
                            </span>
                          </div>
                          <div className="flex items-center justify-end space-x-1">
                            <span className="text-base font-bold text-gray-900 dark:text-white">
                              K{Math.round(estimate.price).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{estimate.distance.toFixed(1)} km</p>
                        </div>

                        {/* Selection Indicator */}
                        {isSelected && (
                          <div className="absolute top-2 right-2">
                            <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })
              )}
            </>
          ) : (
            <>
              {/* Selected ride summary */}
              {selected && (
                <div
                  data-testid="payment-ride-summary"
                  className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-gray-700 border dark:border-gray-600"
                >
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-white">{getRideIcon(selected.vehicle_type)}</div>
                    <div>
                      <h3 className="font-semibold text-sm text-gray-800 dark:text-white">
                        {selectedInfo?.name || selected.vehicle_type}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {Math.round(selected.eta)} min · {selected.distance.toFixed(1)} km
                      </p>
                    </div>
                  </div>
                  <div className="text-base font-bold text-gray-900 dark:text-white">
                    K{Math.round(selected.price).toLocaleString()}
                  </div>
                </div>
              )}

              {/* Payment Method + Cash Warning */}
              <div className="flex items-center justify-between mb-2 mt-1">
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Payment Method</span>
                {hasActiveDiscount && activeDiscountInfo?.discount_percent ? (
                  <span className="text-[10px] font-semibold text-green-700 bg-green-50 dark:bg-green-900/30 dark:text-green-300 px-2 py-0.5 rounded-full">
                    {activeDiscountInfo.discount_percent}% credit available
                  </span>
                ) : null}
              </div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <button
                  type="button"
                  data-testid="payment-method-mobile-money-btn"
                  onClick={() => onPaymentMethodChange('mobile_money')}
                  className={`flex items-center justify-center space-x-1.5 py-2 px-2 rounded-lg border text-xs font-semibold transition-all ${
                    !isCash
                      ? 'bg-green-50 border-green-500 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                      : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                  }`}
                >
                  <Smartphone className="w-3.5 h-3.5" />
                  <span>Mobile Money</span>
                </button>
                <button
                  type="button"
                  data-testid="payment-method-cash-btn"
                  onClick={() => onPaymentMethodChange('cash')}
                  className={`flex items-center justify-center space-x-1.5 py-2 px-2 rounded-lg border text-xs font-semibold transition-all ${
                    isCash
                      ? 'bg-amber-50 border-amber-500 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                      : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                  }`}
                >
                  <Banknote className="w-3.5 h-3.5" />
                  <span>Cash</span>
                </button>
              </div>
              {showCashDiscountWarning && (
                <div
                  data-testid="cash-discount-warning"
                  className="flex items-start space-x-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-2 mb-2"
                >
                  <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] leading-snug text-amber-800 dark:text-amber-200">
                    <strong>Heads up:</strong> Promotions and referral credits cannot be applied to cash trips. Switch to <strong>Mobile Money</strong> to use your {activeDiscountInfo?.discount_percent ? `${activeDiscountInfo.discount_percent}% ` : ''}discount.
                  </p>
                </div>
              )}

              {/* Family credit redemption (manual) */}
              {familyCreditBalance > 0 && (
                <label
                  data-testid="apply-family-credit-row"
                  className="flex items-center justify-between space-x-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-2 mb-2 cursor-pointer"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-semibold text-green-800 dark:text-green-300">
                      Use family credit (K{familyCreditBalance.toLocaleString()} available)
                    </p>
                    <p className="text-[10px] text-green-700/80 dark:text-green-300/80 leading-snug">
                      Redeems credit toward this ride. Disabled by default.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={applyFamilyCredit}
                    onChange={(e) => onApplyFamilyCreditChange(e.target.checked)}
                    data-testid="apply-family-credit-toggle"
                    className="w-4 h-4 accent-green-600"
                  />
                </label>
              )}

              {/* Personal wallet credit redemption */}
              {personalCreditBalance > 0 && (
                <label
                  data-testid="apply-personal-credit-row"
                  className="flex items-center justify-between space-x-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-2 mb-2 cursor-pointer"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-semibold text-emerald-800 dark:text-emerald-300">
                      Use wallet credit (K{personalCreditBalance.toLocaleString()} available)
                    </p>
                    <p className="text-[10px] text-emerald-700/80 dark:text-emerald-300/80 leading-snug">
                      Pay this ride from your personal credit balance.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={applyPersonalCredit}
                    onChange={(e) => onApplyPersonalCreditChange(e.target.checked)}
                    data-testid="apply-personal-credit-toggle"
                    className="w-4 h-4 accent-emerald-600"
                  />
                </label>
              )}
            </>
          )}
        </div>

        {/* Footer - step-dependent action */}
        <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-t dark:border-gray-700 p-3">
          {step === 'ride' ? (
            <Button
              data-testid="ride-continue-btn"
              onClick={() => setStep('payment')}
              disabled={isLoading || estimates.length === 0}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-3 text-base font-semibold rounded-lg"
            >
              Continue{selected ? ` · K${Math.round(selected.price).toLocaleString()}` : ''}
            </Button>
          ) : (
            <Button
              data-testid="confirm-trip-btn"
              onClick={() => onConfirmRide({ ...getSelectedEstimate(), paymentMethod })}
              disabled={isLoading || estimates.length === 0}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-3 text-base font-semibold rounded-lg"
            >
              Confirm Trip {isCash ? '· Cash' : '· Mobile Money'}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default RideSelectionModal;
